import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { resolveUrl, localizedStoreProductName } from "../../../utils/storeUtils";
import { StoreProductPriceBlock } from "./StoreProductPriceBlock";

const apiBaseUrl = axios.defaults.baseURL || "";
const resolveImageUrl = (url) => resolveUrl(url, apiBaseUrl);

function StarRating({ count = 5 }) {
  return (
    <div className="store-testimonial-stars" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: count }, (_, i) => (
        <svg key={i} width="20" height="20" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M10 1l2.5 6.5L19 8l-5 4.5 1.5 6.5L10 15l-5.5 4 1.5-6.5L1 8l6.5-.5L10 1z" />
        </svg>
      ))}
    </div>
  );
}

function VerifiedBadge() {
  return (
    <span className="store-testimonial-verified" aria-label="Verified purchase">
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <circle cx="8" cy="8" r="8" fill="currentColor" />
        <path d="M5 8l2.5 2.5L11 6" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

export default function CustomerTestimonials() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, i18n } = useTranslation("global");
  const lang = i18n.language || "en";
  const isAr = lang === "ar";

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get("/reviews");
        const list = response?.data?.reviews || [];
        setReviews(list.slice(0, 4));
      } catch {
        setReviews([]);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, []);

  const publicBase = process.env.PUBLIC_URL || "";
  const placeholderReviews = [
    {
      _id: "placeholder-1",
      name: "Martin Culhane",
      arName: "مارتن كولهان",
      description: "The ergonomic chair has completely changed how I work. My posture feels better, and I can sit comfortably all day without any back pain.",
      arDescription: "كرسي مريح غير طريقة عملي تماماً. وضعيتي أفضل ويمكنني الجلوس براحة طوال اليوم دون ألم في الظهر.",
      title: "Milano",
      arTitle: "ميلانو",
      image: { url: `${publicBase}/photos/wd-furniture-pt-sofas-opt.webp`, alt: "Sofa", arAlt: "كنبة" },
    },
    {
      _id: "placeholder-2",
      name: "Talan Baptista",
      arName: "تالان بابتيستا",
      description: "Excellent quality and design. The desk setup is sleek, sturdy, and keeps me focused during long hours.",
      arDescription: "جودة وتصميم ممتازان. طاولة المكتب أنيقة ومتينة وتساعدني على التركيز لساعات طويلة.",
      title: "Openest Sprig",
      arTitle: "أوبينست سبرينغ",
      image: { url: `${publicBase}/photos/wd-furniture-pt-chairs-opt.webp`, alt: "Chair", arAlt: "كرسي" },
    },
  ];

  const displayReviews = reviews.length > 0 ? reviews : placeholderReviews;
  if (loading) return null;

  return (
    <section className="store-customer-testimonials" aria-labelledby="store-testimonials-heading">
      <div className="store-customer-testimonials-header">
        <h2 id="store-testimonials-heading" className="store-customer-testimonials-title">
          {t("store.testimonials.title", "What Our Customers Say")}
        </h2>
        <p className="store-customer-testimonials-subtitle">
          {t("store.testimonials.subtitle", "Real stories from people who love our products.")}
        </p>
      </div>

      <div className="store-customer-testimonials-grid">
        {displayReviews.slice(0, 4).map((review) => {
          const product = review?.product;
          const name = isAr ? (review?.arName || review?.name) : (review?.name || review?.arName);
          const quote = isAr ? (review?.arDescription || review?.description) : (review?.description || review?.arDescription);
          const productName = product
            ? localizedStoreProductName(product, lang)
            : isAr
              ? review?.arTitle || review?.title
              : review?.title || review?.arTitle;
          const productPrice =
            product != null ? (
              <StoreProductPriceBlock product={product} />
            ) : (
              t("store.priceMissing", { defaultValue: "—" })
            );
          const sceneImgUrl = resolveImageUrl(review?.image?.url);
          const productThumbUrl = product
            ? resolveImageUrl(product?.images?.[0]?.url || product?.image?.url)
            : sceneImgUrl;
          const productLink = product?._id ? `/store/product/${product._id}` : "/store";

          return (
            <article key={review._id} className="store-testimonial-card">
              <div className="store-testimonial-card-image-wrap">
                <img
                  src={sceneImgUrl}
                  alt={isAr ? (review?.image?.arAlt || review?.image?.alt) : (review?.image?.alt || review?.image?.arAlt)}
                  className="store-testimonial-card-image"
                />
                <Link
                  to={productLink}
                  className="store-testimonial-quick-view"
                  aria-label={product ? "View product" : "View store"}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </Link>
              </div>
              <div className="store-testimonial-card-content">
                <StarRating />
                <p className="store-testimonial-quote">{quote}</p>
                <div className="store-testimonial-customer">
                  <span className="store-testimonial-name">{name}</span>
                  <VerifiedBadge />
                </div>
                <div className="store-testimonial-product">
                  <Link to={productLink} className="store-testimonial-product-link">
                    <img
                      src={productThumbUrl}
                      alt=""
                      className="store-testimonial-product-thumb"
                      aria-hidden="true"
                    />
                    <div className="store-testimonial-product-info">
                      <span className="store-testimonial-product-name">{productName}</span>
                      <span className="store-testimonial-product-price">{productPrice}</span>
                    </div>
                  </Link>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
